// Fetch wrappers for the attachment endpoints (connector registry + per-product
// hooks). Dashboard-facing: these are author tools, so every request is sent
// with credentials:"include" — the dashboard session cookie attaches when the
// editor is loaded same-origin (same auth model as the other embed clients).

import type { CompatibleProductDto, HookConnectorDto, ProductHookDto } from "../types";

function apiBase(baseUrl?: string): string {
  return (baseUrl ?? "").replace(/\/+$/, "");
}

async function throwHttpError(prefix: string, res: Response): Promise<never> {
  const text = await res.text().catch(() => "");
  throw new Error(`${prefix} failed: ${res.status} ${res.statusText} ${text}`);
}

export interface FetchConnectorsOptions {
  /** Base URL of the deployment. Empty = same origin. */
  baseUrl?: string;
  signal?: AbortSignal;
}

/** Global connector registry — populates the inspector's connector dropdown. */
export async function fetchConnectors(opts: FetchConnectorsOptions): Promise<HookConnectorDto[]> {
  const url = `${apiBase(opts.baseUrl)}/api/attachment/connectors`;
  const res = await fetch(url, {
    headers: { Accept: "application/json" },
    credentials: "include",
    signal: opts.signal,
  });
  if (!res.ok) await throwHttpError("fetchConnectors", res);
  return (await res.json()) as HookConnectorDto[];
}

export interface FetchProductHooksOptions {
  baseUrl?: string;
  productId: string;
  signal?: AbortSignal;
}

/** Hooks on a product — the editor's load call. */
export async function fetchProductHooks(opts: FetchProductHooksOptions): Promise<ProductHookDto[]> {
  const url = `${apiBase(opts.baseUrl)}/api/attachment/product/${encodeURIComponent(opts.productId)}/hooks`;
  const res = await fetch(url, {
    headers: { Accept: "application/json" },
    credentials: "include",
    signal: opts.signal,
  });
  if (!res.ok) await throwHttpError("fetchProductHooks", res);
  return (await res.json()) as ProductHookDto[];
}

export interface SaveProductHooksOptions {
  baseUrl?: string;
  productId: string;
  /** The FULL replacement hook set — PUT semantics, not a patch. */
  hooks: ProductHookDto[];
  signal?: AbortSignal;
}

/** Replaces a product's entire hook set (editor save). */
export async function saveProductHooks(opts: SaveProductHooksOptions): Promise<void> {
  const url = `${apiBase(opts.baseUrl)}/api/attachment/product/${encodeURIComponent(opts.productId)}/hooks`;
  const res = await fetch(url, {
    method: "PUT",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "include",
    body: JSON.stringify(opts.hooks),
    signal: opts.signal,
  });
  if (!res.ok) await throwHttpError("saveProductHooks", res);
}

export interface FetchConnectorProductsOptions {
  baseUrl?: string;
  /** The connector's stable string key (connectorKey), not its GUID. */
  connectorKey: string;
  signal?: AbortSignal;
}

/** All products carrying a hook with this connector — the emergent
 *  "what can attach to what" query, computed relationally server-side. */
export async function fetchConnectorProducts(
  opts: FetchConnectorProductsOptions,
): Promise<CompatibleProductDto[]> {
  const url = `${apiBase(opts.baseUrl)}/api/attachment/connectors/${encodeURIComponent(opts.connectorKey)}/products`;
  const res = await fetch(url, {
    headers: { Accept: "application/json" },
    credentials: "include",
    signal: opts.signal,
  });
  if (!res.ok) await throwHttpError("fetchConnectorProducts", res);
  return (await res.json()) as CompatibleProductDto[];
}
